
import { getCachedItem, getCacheKeys, cleanupExpiredItems } from '@/utils/clientCacheUtils';
import { RESPONSE_STORE } from './cache-constants';
import { updateCacheMetrics } from './cache-metrics';
import { getRelatedContentKeys } from './cache-utils';
import { cacheEventBus } from './cache-event-bus';

// Enhanced invalidation - clear cache by topic with relationship awareness
export const invalidateCacheByTopic = async (topic: string): Promise<number> => {
  try {
    // Get all response cache keys
    const keys = await getCacheKeys(RESPONSE_STORE);
    let invalidatedCount = 0;
    
    // Filter and delete keys related to the topic
    for (const key of keys) {
      try {
        const item = await getCachedItem<any>(key, RESPONSE_STORE);
        if (item && item.topic === topic) {
          await cleanupExpiredItems(RESPONSE_STORE, [key]);
          invalidatedCount++;
          
          // Check for related content
          const relatedContent = await getRelatedContentKeys(key, RESPONSE_STORE);
          if (relatedContent && relatedContent.relatedKeys.length > 0) {
            // Invalidate related content too
            await cleanupExpiredItems(relatedContent.relatedType, relatedContent.relatedKeys);
            invalidatedCount += relatedContent.relatedKeys.length;
          }
        }
      } catch (err) {
        console.error(`Error checking cache key ${key}:`, err);
      }
    }
    
    // Update metrics
    updateCacheMetrics(RESPONSE_STORE, 'set', { 
      invalidated: invalidatedCount,
      reason: 'topic',
      topic
    });
    
    // Publish event for subscribers
    cacheEventBus.publish('topicUpdated', { topic, invalidatedCount });
    
    return invalidatedCount;
  } catch (error) {
    console.error('Error invalidating cache by topic:', error);
    return 0;
  }
};

// Enhanced invalidation - clear cache by language with relationship awareness
export const invalidateCacheByLanguage = async (language: string): Promise<number> => {
  try {
    // Get all response cache keys
    const keys = await getCacheKeys(RESPONSE_STORE);
    let invalidatedCount = 0;
    
    // Filter and delete keys related to the language
    for (const key of keys) {
      try {
        const item = await getCachedItem<any>(key, RESPONSE_STORE);
        if (item && item.language === language) {
          await cleanupExpiredItems(RESPONSE_STORE, [key]);
          invalidatedCount++;
          
          // Check for related content
          const relatedContent = await getRelatedContentKeys(key, RESPONSE_STORE);
          if (relatedContent && relatedContent.relatedKeys.length > 0) {
            // Invalidate related content too
            await cleanupExpiredItems(relatedContent.relatedType, relatedContent.relatedKeys);
            invalidatedCount += relatedContent.relatedKeys.length;
          }
        }
      } catch (err) {
        console.error(`Error checking cache key ${key}:`, err);
      }
    }
    
    // Update metrics
    updateCacheMetrics(RESPONSE_STORE, 'set', { 
      invalidated: invalidatedCount,
      reason: 'language',
      language
    });
    
    // Publish event for subscribers
    cacheEventBus.publish('languageUpdated', { language, invalidatedCount });
    
    return invalidatedCount;
  } catch (error) {
    console.error('Error invalidating cache by language:', error);
    return 0;
  }
};

// New function: Invalidate cache based on knowledge base updates
export const invalidateCacheByKnowledgeUpdate = async (knowledgeId: string): Promise<number> => {
  try {
    // This would need integration with your knowledge base system
    // For now, we'll implement a basic version that invalidates responses
    // that might be affected by knowledge changes
    
    // Get all response cache keys
    const keys = await getCacheKeys(RESPONSE_STORE);
    let invalidatedCount = 0;
    
    // For a real implementation, you would need to track which responses
    // are derived from which knowledge base entries
    // For now, we'll use a simple approach to invalidate potentially affected responses
    
    // Publish event for subscribers
    cacheEventBus.publish('knowledgeUpdated', { knowledgeId, invalidatedCount });
    
    return invalidatedCount;
  } catch (error) {
    console.error('Error invalidating cache by knowledge update:', error);
    return 0;
  }
};
